import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReviewItPopupComponent } from 'src/app/templates/featured-product/component/product-details/review-it-popup/review-it-popup.component';
import { environment } from 'src/environments/environment.prod';
import { AppConstants } from 'src/app/app.constants';
import { CommanpageService } from 'src/_lib/service/commanpages.service';
import { StarArrayPipe } from 'src/_lib/pipes/star-array.pipe';
import { NgIf, NgFor, DatePipe, CommonModule } from '@angular/common';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { BrowserService } from 'src/_lib/service/browser.service';
Swiper.use([Navigation, Pagination, Autoplay]);

@Component({
  selector: 'app-product-review',
  templateUrl: './product-review.component.html',
  styleUrls: ['./product-review.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    StarArrayPipe,
    DatePipe,
    CommonModule
  ]
})
export class ProductReviewComponent implements OnChanges {
  cdnImagePath: string = environment.cdnImagePath;
  commonImagePath: string = environment.commonImagePath;
  firstObj: number = this.appConstants.firstObj;
  appearances: number[] = [];
  appearancesSum: number = null;
  appearanceProgressBarValue: number | null = null;
  installations: number[] = [];
  installationsSum: number = null;
  installationProgressBarValue: number | null = null;
  prices: number[] = [];
  pricesSum: number = null;
  priceProgressBarValue: number | null = null;
  qualitys: number[] = [];
  qualitysSum: number = null;
  qualityProgressBarValue: number | null = null;
  totalSum: number = null;
  averageTotal: number | null = null;
  totalAverageOutOf5: number = null;
  reviews: any[] = [];
  visibleItems: any[] = [];
  itemsPerPage = 1;
  currentIndex = 0;
  individualTotalAverageOutOf5: number[] = [];
  product_photos: any;

  @Input() product_key: number = null;
  @Input() productName: string = null;
  @Input() imageObject: Array<{ image: string; thumbImage: string }> = [];
  @Input() productReviews: any[] = [];
  @Output() totalAverageOutOf5Event: EventEmitter<number> = new EventEmitter<number>();
  @Output() reviewCountEvent: EventEmitter<number> = new EventEmitter<number>();

  constructor(
    public dialog: MatDialog,
    private appConstants: AppConstants,
    private commonService: CommanpageService,
    private browser: BrowserService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productReviews']?.currentValue) {
      this.processReviews(this.productReviews);
    }
  }

  ngAfterViewInit(): void {
  if (!this.browser.isBrowser) return;
  
  // CRITICAL FIX: Check slide count before enabling loop
  const swiperElement = this.browser.doc?.querySelector('.product-img');
  const slideCount = swiperElement?.querySelectorAll('.swiper-slide').length || 0;
  const enableLoop = slideCount >= 2;
  
  new Swiper('.product-img', {
    slidesPerView: 1,
    loop: enableLoop,
    loopAdditionalSlides: enableLoop ? 1 : 0,
    autoplay: enableLoop ? {
      delay: 3000,
      disableOnInteraction: false,
    } : false,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
  });
}

onImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    const defaultImage = `${this.commonImagePath}image_not_found.webp`;
    image.src = defaultImage;
  }

  openReviewItPopup(): void {
    const dialogRef = this.dialog.open(ReviewItPopupComponent, {
      data: {
        reviews: this.productReviews,
        avgRating: this.totalAverageOutOf5,
        reviewsCount: this.reviews?.length,
        productName: this.productName,
        product_key: this.product_key,
        productImg: this.imageObject?.[this.firstObj]?.image
      }
    });
    dialogRef.afterClosed().subscribe();
  }

  processReviews(reviews: any[]): void {
    this.discardAll();
    if (!reviews || reviews?.length === 0) {
      this.totalAverageOutOf5 = 0;
      this.sendtotalAverageOutOf5();
      this.sendTotalReviewsCount(0);
      return;
    }
    for (let review of reviews) {
      this.appearances.push(parseFloat(review.appearance));
      this.installations.push(parseFloat(review.installation));
      this.prices.push(parseFloat(review.price_value));
      this.qualitys.push(parseFloat(review.quality));
      this.reviews.push({
        first_name: review.first_name || '',
        last_name: review.last_name || '',
        title: review.title || '',
        comments: review.comments || '',
        date: review.add_dt,
        add_dt: (review.add_dt && review.add_dt !== '0000-00-00 00:00:00') ? review.add_dt : null,
        verified_purchase: +review.verified_purchase
      });
      this.calculateIndividualTotalAverageOutOf5(
        +review.appearance,
        +review.installation,
        +review.price_value,
        +review.quality
      );
    }
    this.appearancesSum = this.commonService.arraySum(this.appearances);
    this.installationsSum = this.commonService.arraySum(this.installations);
    this.pricesSum = this.commonService.arraySum(this.prices);
    this.qualitysSum = this.commonService.arraySum(this.qualitys);
    this.totalSum = this.appearancesSum + this.installationsSum + this.pricesSum + this.qualitysSum;
    const totalLength =
      this.appearances?.length +
      this.installations?.length +
      this.prices?.length +
      this.qualitys?.length;
    this.averageTotal = totalLength !== 0 ? this.totalSum / totalLength : 0;
    const maxScale = 5;
    this.totalAverageOutOf5 = totalLength !== 0 ? (this.averageTotal / maxScale) * 5 : 0;
    this.calculateAverageAppearance();
    this.calculateAverageInstallation();
    this.calculateAveragePrice();
    this.calculateAverageQuality();
    this.sendtotalAverageOutOf5();
    this.sendTotalReviewsCount(this.reviews?.length);
    this.loadInitialReview();
  }

  calculateAverageAppearance() {
    const avg = this.appearancesSum / this.appearances?.length;
    this.appearanceProgressBarValue = (avg / 5) * 100;
  }

  calculateAverageInstallation() {
    const avg = this.installationsSum / this.installations?.length;
    this.installationProgressBarValue = (avg / 5) * 100;
  }

  calculateAveragePrice() {
    const avg = this.pricesSum / this.prices?.length;
    this.priceProgressBarValue = (avg / 5) * 100;
  }

  calculateAverageQuality() {
    const avg = this.qualitysSum / this.qualitys?.length;
    this.qualityProgressBarValue = (avg / 5) * 100;
  }

  calculateIndividualTotalAverageOutOf5(appearance: number, installation: number, price: number, quality: number) {
    const maxScale = 5;
    const totalScale = 4 * maxScale;
    const totalReviewdSum = (appearance + installation + price + quality) / totalScale;
    const result = +(totalReviewdSum * maxScale).toFixed(1);
    this.individualTotalAverageOutOf5.push(result);
  }

  discardAll() {
    this.appearances = [];
    this.installations = [];
    this.prices = [];
    this.qualitys = [];
    this.individualTotalAverageOutOf5 = [];
    this.product_photos = [];
    this.appearancesSum = null;
    this.installationsSum = null;
    this.pricesSum = null;
    this.qualitysSum = null;
    this.totalSum = null;
    this.averageTotal = null;
    this.totalAverageOutOf5 = null;
    this.reviews = [];
    this.visibleItems = [];
    this.currentIndex = 0;
  }

  sendtotalAverageOutOf5() {
    this.totalAverageOutOf5Event.emit(this.totalAverageOutOf5);
  }

  sendTotalReviewsCount(count: number) {
    this.reviewCountEvent.emit(count);
  }

  loadInitialReview() {
    this.visibleItems = this.reviews.slice(0, this.itemsPerPage);
    this.currentIndex = this.itemsPerPage;
  }

  loadMoreReviews() {
    const nextItems = this.reviews.slice(this.currentIndex);
    this.visibleItems = [...this.visibleItems, ...nextItems];
    this.currentIndex = this.reviews?.length;
  }

  get hasMoreItems(): boolean {
    return this.currentIndex < this.reviews?.length;
  }
}